/**
 * Static post generation: dispatcher + poller for the async background job
 * workflow. POST /static-post/generate returns HTTP 202 with
 * status="pending"; the final markup/output path surfaces only via
 * GET /static-post/status/{campaign_id}. Unlike carousel/video this job is
 * keyed by campaign_id, not post_id. Mirrors carouselGeneration.ts's shape.
 */
import type {
  StaticPostAlreadyGeneratedResponse,
  StaticPostGenerateRequest,
  StaticPostGenerateResponse,
  ImageOption,
} from '../types/staticPost';

export const STATIC_POST_POLL_INTERVAL_MS = 3000;
// A GPT Image 2 (high quality) render takes ~100 s and the quality gate may
// retry it, so allow room for a few attempts before telling the user it timed out.
export const STATIC_POST_POLL_TIMEOUT_MS = 8 * 60 * 1000;
export const STATIC_POST_MAX_CONSECUTIVE_ERRORS = 3;

/** Minimal surface of the API client this service needs. */
export interface StaticPostApiClient {
  generateStaticPoster(
    request: StaticPostGenerateRequest
  ): Promise<StaticPostGenerateResponse | StaticPostAlreadyGeneratedResponse>;
  getStaticPostStatus(campaignId: string): Promise<StaticPostGenerateResponse>;
}

export interface DispatchResult {
  /** Resolved campaign_id the background job runs against. */
  campaignId: string | null;
  /** Raw POST response — status is expected to be "pending" on 202. */
  response: StaticPostGenerateResponse | StaticPostAlreadyGeneratedResponse;
}

/**
 * Dispatch the generation job. Resolves once the POST returns (202), i.e.
 * the job has been accepted and runs in the background — it does NOT wait
 * for the poster. Throws on transport/HTTP errors so callers can surface a
 * message.
 */
export async function dispatchStaticPostGeneration(
  api: StaticPostApiClient,
  request: StaticPostGenerateRequest
): Promise<DispatchResult> {
  const response = await api.generateStaticPoster(request);
  const campaignId = response && 'campaign_id' in response ? response.campaign_id : null;
  return { campaignId, response };
}

export interface PollStaticPostStatusOptions {
  campaignId: string;
  /** Cadence between status checks. Defaults to 3s (usually faster than video/carousel). */
  intervalMs?: number;
  /** Max wait before giving up and firing onTimeout. Defaults to 3 min. */
  timeoutMs?: number;
  /** Consecutive HTTP/network errors tolerated before onError. Defaults to 3. */
  maxConsecutiveErrors?: number;
  /** Fired on every successful status read (useful for debug/tests). */
  onStatus?: (status: StaticPostGenerateResponse) => void;
  /**
   * status === "done". imageOptions/selectedImageIndex are set only when
   * the quality gate exhausted every attempt — a caveat on an otherwise-
   * done job, not an error.
   */
  onDone: (
    outputPath: string | null,
    content: string | null,
    imageOptions?: ImageOption[] | null,
    selectedImageIndex?: number | null
  ) => void;
  /** status === "error", a 404 (campaign missing), or too many consecutive polling errors. */
  onError: (message: string) => void;
  /** Polled for the full timeout window without a terminal state. */
  onTimeout: () => void;
}

/**
 * Poll the status endpoint until the job reaches a terminal state, the
 * timeout elapses, or cancellation. Returns a cancel() handle. Same
 * stopping semantics as pollCarouselStatus.
 */
export function pollStaticPostStatus(
  api: StaticPostApiClient,
  options: PollStaticPostStatusOptions
): () => void {
  const {
    campaignId,
    intervalMs = STATIC_POST_POLL_INTERVAL_MS,
    timeoutMs = STATIC_POST_POLL_TIMEOUT_MS,
    maxConsecutiveErrors = STATIC_POST_MAX_CONSECUTIVE_ERRORS,
    onStatus,
    onDone,
    onError,
    onTimeout,
  } = options;

  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let consecutiveErrors = 0;
  const startedAt = Date.now();

  const stop = () => {
    cancelled = true;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const isExpired = () => Date.now() - startedAt >= timeoutMs;

  const scheduleNext = () => {
    if (cancelled) return;
    timer = setTimeout(() => { void tick(); }, intervalMs);
  };

  const tick = async () => {
    if (cancelled) return;
    if (isExpired()) {
      onTimeout();
      stop();
      return;
    }
    try {
      const status = await api.getStaticPostStatus(campaignId);
      if (cancelled) return;
      consecutiveErrors = 0;
      onStatus?.(status);

      if (status.status === 'done') {
        if (status.output_path || status.content) {
          onDone(status.output_path, status.content, status.image_options, status.selected_image_index);
        } else {
          onError('Generation completed, but no output was returned.');
        }
        stop();
        return;
      }

      if (status.status === 'error') {
        onError(status.error_message || 'Generation failed. Your credits were refunded automatically.');
        stop();
        return;
      }

      // status === "pending" — worker still running
      scheduleNext();
    } catch (err: any) {
      if (cancelled) return;
      // Campaign no longer exists — nothing to wait on, stop immediately.
      if (err?.response?.status === 404) {
        onError('This generation job could not be found.');
        stop();
        return;
      }
      consecutiveErrors += 1;
      if (consecutiveErrors >= maxConsecutiveErrors) {
        onError(
          'Could not check the generation status. Please try again in a moment.'
        );
        stop();
        return;
      }
      scheduleNext();
    }
  };

  void tick();
  return stop;
}
